import { nextTick, Ref, ref } from 'vue'
import { useVisitorsStore } from '@/shared/store'
import { GENDERS } from '@/shared/constants/other.constants'
import type { VisitorType } from '@/shared/types/store.types'
import type { CountryWithDetailsType } from '@/shared/types/other.types'
import type { OcrSignals } from './useOcrSignals'

interface UseOcrFlowOptions {
  isAvailable: boolean
  signals: OcrSignals
  person: Ref<VisitorType>
  selectedNationality: Ref<CountryWithDetailsType | undefined>
  isHolder: boolean
  resetOcrData: () => void
  resetValidation: () => void
  touchPassportExpiry: () => void
  onSendCloseOcrModal: (
    isClickBtn: boolean,
    isChangeNationality: boolean,
    btnTitle?: string
  ) => void
}

export const useOcrFlow = (options: UseOcrFlowOptions) => {
  const visitorsStore = useVisitorsStore()
  const { signals } = options

  const isNationalityChangeModalVisible = ref(false)
  const previousSelectedNationality = ref<CountryWithDetailsType | undefined>()
  const isNationalityChangedOnClose = ref(false)

  const isRemoveOcrModalVisible = ref(false)
  const ocrRemoveResolver = ref<((value: boolean) => void) | null>(null)

  const toggleOcrManualMode = (): void => {
    signals.isOcrManualMode.value = !signals.isOcrManualMode.value
    options.resetValidation()
    if (signals.isOcrManualMode.value) {
      signals.ocr.cancelRequest()
      // TODO: this is hack, resolve in the qic-connect-ds
      signals.ocr.uploadResult.value = undefined
    }
  }

  const checkOcrManualMode = (): void => {
    const p = options.person.value
    if (
      signals.isNotOcrManualMode.value &&
      p.firstName &&
      p.lastName &&
      p.birthDate &&
      p.passport &&
      p.country &&
      p.nationality
    ) {
      toggleOcrManualMode()
    }
  }

  const checkOcrNationality = (nationality: string | null | undefined): boolean =>
    Boolean(
      options.isAvailable &&
        options.isHolder &&
        visitorsStore.lastHolderNationality &&
        nationality &&
        nationality !== visitorsStore.lastHolderNationality
    )

  const openNationalityChangeModal = (): void => {
    isNationalityChangeModalVisible.value = true
  }

  const openRemoveOcrModal = (): Promise<boolean> => {
    isRemoveOcrModalVisible.value = true
    return new Promise((resolve) => {
      ocrRemoveResolver.value = resolve
    })
  }

  const closeNationalityChangeModal = (
    closeCallback?: () => void,
    keepNationality?: boolean
  ): void => {
    if (keepNationality) {
      options.selectedNationality.value = previousSelectedNationality.value
      options.person.value.nationality = options.selectedNationality.value?.code ?? null
      isNationalityChangedOnClose.value = true
      options.onSendCloseOcrModal(!!closeCallback, true, closeCallback ? 'keep' : undefined)
    } else {
      signals.ocr.cancelRequest(options.resetOcrData)
      signals.ocr.uploadResult.value = undefined
      options.person.value.nationality = options.selectedNationality.value?.code ?? null
      options.onSendCloseOcrModal(true, true, 'change')
    }
    isNationalityChangeModalVisible.value = false
    closeCallback?.()
  }

  const closeRemoveOcrModal = (closeCallback?: () => void, keepOcr?: boolean): void => {
    if (keepOcr) {
      ocrRemoveResolver.value?.(false)
      options.onSendCloseOcrModal(!!closeCallback, false, closeCallback ? 'keep' : undefined)
    } else {
      signals.ocr.uploadResult.value = undefined
      ocrRemoveResolver.value?.(true)
      options.onSendCloseOcrModal(true, false, 'remove')
    }
    ocrRemoveResolver.value = null
    isRemoveOcrModalVisible.value = false
    closeCallback?.()
  }

  const handleProcessFile = async (file: File): Promise<void> => {
    options.resetOcrData()
    const res = await signals.ocr.processFile(file)
    if (!res) return
    const p = options.person.value
    p.lastName = res.familyName
    p.firstName = res.firstName
    p.passport = res.passportNumber
    p.gender = res.gender === 'male' ? GENDERS.MALE : GENDERS.FEMALE
    p.birthDate = res.dateOfBirth
    p.passportExpiryDate = res.dateOfExpiry
    await nextTick()
    options.touchPassportExpiry()
  }

  let uploaderClickSent = false
  const clickUploader = (onFire: () => void): void => {
    if (uploaderClickSent) return
    uploaderClickSent = true
    onFire()
    setTimeout(() => {
      uploaderClickSent = false
    }, 100)
  }

  return {
    isNationalityChangeModalVisible,
    isRemoveOcrModalVisible,
    previousSelectedNationality,
    isNationalityChangedOnClose,
    toggleOcrManualMode,
    checkOcrManualMode,
    checkOcrNationality,
    openNationalityChangeModal,
    openRemoveOcrModal,
    closeNationalityChangeModal,
    closeRemoveOcrModal,
    handleProcessFile,
    clickUploader,
  }
}
